import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, ModalHeader, ModalFooter, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import PreventPageLeave from '../PreventPageLeave/PreventPageLeave';
import { throttle } from 'lodash';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onHeaderClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  preventLeave: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fade: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  keyboard: PropTypes.bool,
  translate: PropTypes.func,
  showSectionTitles: PropTypes.bool
};

const defaultProps = {
  header: '',
  preventLeave: false,
  className: '',
  size: 'medium',
  fade: true,
  backdrop: true,
  keyboard: true,
  showSectionTitles: true,
  confirmDisabled: false,
  translate: key => {
    // Default translations if translate function not provided
    const translations = {
      general_cancel: 'Cancel',
      general_save: 'Save'
    };
    return translations[key] || key;
  }
};

const FloatingModal = ({
  isOpen,
  toggleModal,
  header,
  onHeaderClick,
  children,
  footer,
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  confirmDisabled,
  preventLeave,
  className,
  size,
  fade,
  backdrop,
  keyboard,
  translate,
  showSectionTitles,
  ...otherProps
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [sections, setSections] = useState([]);
  const modalBodyRef = useRef(null);
  const sectionRefs = useRef([]);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    if (children && React.Children.count(children) > 0) {
      const sectionElements = React.Children.toArray(children).filter(
        child =>
          child.type === 'section' ||
          (child.props &&
            child.props.className &&
            child.props.className.includes('modal-section'))
      );

      const sectionData = sectionElements.map((section, index) => ({
        id: section.props?.id || `section-${index}`,
        title:
          section.props?.title ||
          section.props?.['data-title'] ||
          `Section ${index + 1}`,
        hasTitle: !!(section.props?.title || section.props?.['data-title']),
        className: section.props?.className || '',
        element: section
      }));

      setSections(sectionData);
      sectionRefs.current = new Array(sectionData.length);
    }
  }, [children]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = e => {
      if (keyboard && e.key === 'Escape' && isOpen) {
        if (onCancel) {
          onCancel();
        } else {
          toggleModal();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, keyboard, onCancel, toggleModal]);

  const handleScroll = useCallback(() => {
    const throttledFn = throttle(() => {
      if (isScrollingRef.current || !modalBodyRef.current) return;

      const modalBody = modalBodyRef.current;
      const modalHeight = modalBody.clientHeight;
      const scrollTop = modalBody.scrollTop;
      const scrollHeight = modalBody.scrollHeight;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Check if we're at the bottom of the scroll area
      const isAtBottom = scrollTop + modalHeight >= scrollHeight - 10; // 10px tolerance

      if (isAtBottom) {
        scrollTimeoutRef.current = setTimeout(() => {
          setActiveTab(sections.length - 1);
        }, 100);
        return;
      }

      let mostVisibleSection = 0;
      let maxVisibility = 0;

      sectionRefs.current.forEach((sectionRef, index) => {
        if (!sectionRef) return;

        const rect = sectionRef.getBoundingClientRect();
        const modalRect = modalBody.getBoundingClientRect();

        const sectionTop = rect.top - modalRect.top;
        const sectionBottom = rect.bottom - modalRect.top;

        const visibleTop = Math.max(0, sectionTop);
        const visibleBottom = Math.min(modalHeight, sectionBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        const visibility = visibleHeight / rect.height;

        const distanceFromTop = Math.abs(sectionTop);
        const topBonus = distanceFromTop < 50 ? 0.3 : 0;

        const adjustedVisibility = visibility + topBonus;

        if (adjustedVisibility > maxVisibility) {
          maxVisibility = adjustedVisibility;
          mostVisibleSection = index;
        }
      });

      // Also check which section's top is closest to the scroll position
      let closestSectionToTop = 0;
      let minDistance = Infinity;

      sectionRefs.current.forEach((sectionRef, index) => {
        if (!sectionRef) return;

        const rect = sectionRef.getBoundingClientRect();
        const modalRect = modalBody.getBoundingClientRect();
        const sectionTop = rect.top - modalRect.top;

        // Distance from the top of the viewport
        const distance = Math.abs(sectionTop);

        if (distance < minDistance && sectionTop <= 100) {
          // Within 100px of top
          minDistance = distance;
          closestSectionToTop = index;
        }
      });

      // Use the section closest to top if it's very close (within 30px)
      const finalSection =
        minDistance < 30 ? closestSectionToTop : mostVisibleSection;

      scrollTimeoutRef.current = setTimeout(() => {
        setActiveTab(finalSection);
      }, 50);
    }, 300);

    return throttledFn();
  }, [sections.length, setActiveTab]);

  useEffect(() => {
    setActiveTab(0);

    if (!isOpen) return;

    const setupScrollListener = () => {
      const modalBody = modalBodyRef.current;
      if (modalBody) {
        modalBody.addEventListener('scroll', handleScroll, {
          capture: true,
          passive: true
        });
      }
    };

    const removeScrollListener = () => {
      const modalBody = modalBodyRef.current;
      if (modalBody) {
        modalBody.removeEventListener('scroll', handleScroll, {
          capture: true
        });
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };

    // Add listener when modal opens
    const timer = setTimeout(setupScrollListener, 500);

    // Cleanup function - removes listener when modal closes or component unmounts
    return () => {
      clearTimeout(timer);
      removeScrollListener();
    };
  }, [handleScroll, isOpen]);

  const handleTabClick = index => {
    if (!sectionRefs.current[index]) return;

    isScrollingRef.current = true;
    setActiveTab(index);

    sectionRefs.current[index].scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

    // Reset scroll flag after animation
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 600);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      toggleModal();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'medium';
      default:
        return 'large';
    }
  };

  const modalClassName =
    `modal-wrapper-new floating-modal ${getSizeClass()} ${className}`.trim();

  // Determine if we should show tabs and section headers
  const shouldShowTabs = sections.length > 1;
  const shouldShowSectionHeaders =
    showSectionTitles && !(sections.length === 1 && !sections[0]?.hasTitle);

  return (
    <PreventPageLeave prevent={preventLeave}>
      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        className={modalClassName}
        centered={true}
        fade={fade}
        backdrop={backdrop}
        keyboard={false}
        {...otherProps}
      >
        <ModalHeader
          toggle={toggleModal}
          className={onHeaderClick ? 'cursor-pointer' : ''}
          onClick={onHeaderClick}
        >
          {typeof header === 'string' ? <span>{header}</span> : header}
        </ModalHeader>
        {shouldShowTabs && (
          <div className="floating-modal-tabs">
            {sections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                className={`floating-modal-tab ${
                  index === activeTab ? 'active' : ''
                } ${section.className || ''}`}
                onClick={() => handleTabClick(index)}
              >
                {section.title}
              </button>
            ))}
          </div>
        )}
        <div ref={modalBodyRef} className="modal-body floating-modal-body">
          {sections.map((section, index) => (
            <div
              key={section.id}
              ref={el => (sectionRefs.current[index] = el)}
              className={`floating-modal-section${
                shouldShowSectionHeaders ? ' with-title' : ''
              } ${section.className || ''}`}
              data-section-index={index}
            >
              {shouldShowSectionHeaders && (
                <div className="floating-modal-section-header">
                  <h3>{section.title}</h3>
                </div>
              )}
              {section.element}
            </div>
          ))}
        </div>
        <ModalFooter className="floating-modal-footer">
          {footer ? (
            footer
          ) : (
            <div className="floating-modal-actions">
              {(onCancel || !onConfirm) && (
                <Button color="secondary" onClick={handleCancel}>
                  <i className="fal fa-times-circle" />
                  {cancelLabel || translate('general_cancel')}
                </Button>
              )}
              {onConfirm && (
                <Button
                  color="orange"
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                >
                  <i className="far fa-check" />
                  {confirmLabel || translate('general_save')}
                </Button>
              )}
            </div>
          )}
        </ModalFooter>
      </Modal>
    </PreventPageLeave>
  );
};

FloatingModal.propTypes = propTypes;
FloatingModal.defaultProps = defaultProps;

export default FloatingModal;
